import { Card, CardHeader, CardBody, CardFooter } from "@heroui/react";
import { useDataTable } from "@/hooks";
import {
    DataTableControlBar,
    DataTableSkeleton,
    DataTablePagination,
    FilterButton,
} from "@/components/data-table";
import {
    EmptyState,
    ErrorState,
    PageHeader,
    ThemedText,
} from "@/components/ui";
import { formatCurrency } from "@/utils";
import { RuleFilterPanel } from "./components";

const initialFilters = {
    rules: "",
};

const GeneralJournalData = () => {
    const table = useDataTable(
        "general-journal/data",
        null,
        {},
        initialFilters,
    );

    // grand_total is appended to paginator via Laravel's additional()
    const grandTotal = table.apiResponse?.grand_total || {
        debit: 0,
        credit: 0,
    };

    const rulesCount = (() => {
        try {
            const parsed = JSON.parse(table.filters.values.rules || "[]");
            return parsed.length;
        } catch {
            return 0;
        }
    })();

    const skeletonColumns = [
        { name: "No", skeletonWidth: "w-12" },
        { name: "Info Jurnal / Deskripsi", skeletonWidth: "w-40" },
        { name: "Akun", skeletonWidth: "w-64" },
        { name: "Debit", skeletonWidth: "w-32", align: "end" },
        { name: "Credit", skeletonWidth: "w-32", align: "end" },
    ];

    if (table.error) {
        return (
            <div className="p-6">
                <ErrorState
                    title="Gagal Memuat Data"
                    description="Terjadi kesalahan saat memuat data Jurnal Umum."
                    onAction={table.refetch}
                />
            </div>
        );
    }

    // Build flat rows: each journal shows its items inline
    const buildRows = () => {
        const rows = [];

        table.items.forEach((journal, journalIdx) => {
            const items = journal.items || [];
            const startNo =
                ((table.pagination?.currentPage || 1) - 1) *
                    (table.pagination?.perPage || 10) +
                journalIdx +
                1;

            items.forEach((item, itemIdx) => {
                rows.push({
                    key: `${journal.id}-${item.id}`,
                    isFirstRow: itemIdx === 0,
                    isLastRow: itemIdx === items.length - 1,
                    journalNo: startNo,
                    journal,
                    item,
                    itemCount: items.length,
                });
            });

            // If no items, still show the journal header
            if (items.length === 0) {
                rows.push({
                    key: `${journal.id}-empty`,
                    isFirstRow: true,
                    isLastRow: true,
                    journalNo: startNo,
                    journal,
                    item: null,
                    itemCount: 0,
                });
            }
        });

        return rows;
    };

    const rows = !table.isLoading ? buildRows() : [];

    return (
        <div className="flex h-full w-full flex-col gap-4">
            <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
                <PageHeader
                    title="Jurnal Umum"
                    breadcrumbs={[
                        { label: "Dashboard", href: "/" },
                        { label: "Jurnal Umum" },
                    ]}
                />
                <div className="flex items-center gap-2">
                    <FilterButton
                        isOpen={table.filterPanel.isOpen}
                        onToggle={() =>
                            table.filterPanel.setIsOpen((prev) => !prev)
                        }
                        activeCount={rulesCount}
                    />
                </div>
            </div>

            {/* Filter Panel */}
            <RuleFilterPanel
                isOpen={table.filterPanel.isOpen}
                currentRulesJson={table.filters.values.rules}
                onApply={(rulesJson) => {
                    table.filters.setFilter("rules", rulesJson);
                }}
                onClear={() => {
                    table.filters.setFilter("rules", "");
                }}
            />

            <Card className="border-default-200 border" shadow="none">
                <CardHeader>
                    <DataTableControlBar table={table} hideDelete />
                </CardHeader>
                <CardBody className="p-0">
                    {table.isLoading ? (
                        <DataTableSkeleton
                            columns={skeletonColumns}
                            rowCount={table.pagination.perPage}
                        />
                    ) : table.items.length === 0 ? (
                        <EmptyState
                            title={
                                table.filters.activeCount > 0
                                    ? "Data Tidak Ditemukan"
                                    : "Belum Ada Data"
                            }
                            description={
                                table.filters.activeCount > 0
                                    ? "Tidak ada data yang cocok dengan filter."
                                    : "Belum ada data Jurnal Umum."
                            }
                        />
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="bg-default-100 border-default-200 border-b">
                                        <th className="text-default-500 w-16 px-4 py-3 text-left text-xs font-medium">
                                            No.
                                        </th>
                                        <th className="text-default-500 w-48 px-4 py-3 text-left text-xs font-medium">
                                            Info Jurnal / Deskripsi
                                        </th>
                                        <th className="text-default-500 px-4 py-3 text-left text-xs font-medium">
                                            Akun
                                        </th>
                                        <th className="text-default-500 w-44 px-4 py-3 text-right text-xs font-medium">
                                            Debit
                                        </th>
                                        <th className="text-default-500 w-44 px-4 py-3 text-right text-xs font-medium">
                                            Credit
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((row) => (
                                        <tr
                                            key={row.key}
                                            className={`${row.isFirstRow ? "border-t-default-200 border-t-2" : ""} ${row.isLastRow ? "border-b-default-200 border-b-2" : ""}`}
                                        >
                                            {/* No + Journal Info (rowSpan on first row only) */}
                                            {row.isFirstRow && (
                                                <>
                                                    <td
                                                        className="px-4 py-2 align-top"
                                                        rowSpan={
                                                            row.itemCount || 1
                                                        }
                                                    >
                                                        <ThemedText size="sm">
                                                            {row.journalNo}.
                                                        </ThemedText>
                                                    </td>
                                                    <td
                                                        className="border-default-200 border-l px-4 py-2 align-top"
                                                        rowSpan={
                                                            row.itemCount || 1
                                                        }
                                                    >
                                                        <div className="flex flex-col">
                                                            <ThemedText
                                                                size="sm"
                                                                className="font-semibold"
                                                            >
                                                                {
                                                                    row.journal
                                                                        .journal_number
                                                                }
                                                            </ThemedText>
                                                            <ThemedText
                                                                size="xs"
                                                                className="text-default-400"
                                                            >
                                                                {
                                                                    row.journal
                                                                        .date
                                                                }
                                                            </ThemedText>
                                                            <ThemedText
                                                                size="xs"
                                                                className="text-default-400"
                                                            >
                                                                {
                                                                    row.journal
                                                                        .description
                                                                }
                                                            </ThemedText>
                                                        </div>
                                                    </td>
                                                </>
                                            )}

                                            {/* Account / Description */}
                                            <td className="border-default-200 border-b border-l px-4 py-2">
                                                {row.item && (
                                                    <div className="flex flex-col">
                                                        <ThemedText
                                                            size="sm"
                                                            className={
                                                                Number(
                                                                    row.item
                                                                        .is_debit,
                                                                ) === 0
                                                                    ? "pl-8"
                                                                    : ""
                                                            }
                                                        >
                                                            {row.item.account
                                                                ?.name || "-"}
                                                        </ThemedText>
                                                        {row.item
                                                            .description && (
                                                            <ThemedText
                                                                size="xs"
                                                                className={`text-default-400 ${Number(row.item.is_debit) === 0 ? "pl-8" : ""}`}
                                                            >
                                                                {
                                                                    row.item
                                                                        .description
                                                                }
                                                            </ThemedText>
                                                        )}
                                                    </div>
                                                )}
                                            </td>

                                            {/* Debit */}
                                            <td className="border-default-200 border-b border-l px-4 py-2 text-right">
                                                {row.item &&
                                                    Number(
                                                        row.item.is_debit,
                                                    ) === 1 && (
                                                        <ThemedText size="sm">
                                                            {formatCurrency(
                                                                row.item.amount,
                                                            )}
                                                        </ThemedText>
                                                    )}
                                            </td>

                                            {/* Credit */}
                                            <td className="border-default-200 border-b border-l px-4 py-2 text-right">
                                                {row.item &&
                                                    Number(
                                                        row.item.is_debit,
                                                    ) === 0 && (
                                                        <ThemedText size="sm">
                                                            {formatCurrency(
                                                                row.item.amount,
                                                            )}
                                                        </ThemedText>
                                                    )}
                                            </td>
                                        </tr>
                                    ))}

                                    {/* Grand Total Row */}
                                    <tr className="bg-default-100 border-default-200 border-t-2 font-semibold">
                                        <td className="px-4 py-3" />
                                        <td className="px-4 py-3" />
                                        <td className="px-4 py-3">
                                            <ThemedText
                                                size="sm"
                                                className="font-bold"
                                            >
                                                Grand Total
                                            </ThemedText>
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <ThemedText
                                                size="sm"
                                                className="font-bold"
                                            >
                                                {formatCurrency(
                                                    grandTotal.debit,
                                                )}
                                            </ThemedText>
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <ThemedText
                                                size="sm"
                                                className="font-bold"
                                            >
                                                {formatCurrency(
                                                    grandTotal.credit,
                                                )}
                                            </ThemedText>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardBody>
                <CardFooter>
                    <DataTablePagination table={table} />
                </CardFooter>
            </Card>
        </div>
    );
};

export default GeneralJournalData;
